import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How long a textfield's blur camera waits before it plays. A click on another
 * control blurs the field FIRST and only then fires that control's own camera
 * request (mousedown → blur → click), so the blur camera is deferred and any
 * camera request arriving in the meantime cancels it: the customer sees the new
 * target's animation, never a zoom-out followed by a zoom-in.
 */
export const BLUR_CAMERA_DELAY_MS = 350;

/**
 * On mobile the DS Input defers a text field's blur (~150 ms, so a tap on a
 * dropdown option is not mistaken for "Done"), which means the tapped control's
 * camera can fire BEFORE the field's blur arrives. A blur that lands this soon
 * after another control's camera is that interaction's side effect and is ignored.
 */
export const BLUR_AFTER_FOCUS_IGNORE_MS = 700;

/**
 * Camera requests for the 3D preview, driven by the configurator controls.
 *
 *  - focusThreeD(slug)  — the customer uses a control: play its `view` preset
 *                         (binding.view) and drop any pending blur camera. A
 *                         control with no camera of its own is not a camera
 *                         event — a pending blur camera still plays.
 *  - blurThreeD(slug)   — a textfield lost focus (DS Input onBlur, and on mobile
 *                         the typing-mode exit `onMobileFocusChange(false)` — the
 *                         keyboard closing): play its `blur_view` preset
 *                         (binding.blur_view) after BLUR_CAMERA_DELAY_MS unless
 *                         another camera request lands first (or just did).
 *
 * `threeD` is the buildThreeDProps() result ({ views, blurViews }).
 * Returns { threeDFocus: {view, tick}, focusThreeD, blurThreeD } — `tick` bumps
 * so the same preset can be re-requested.
 */
export default function useThreeDCamera(threeD) {
  const [threeDFocus, setThreeDFocus] = useState({ view: null, tick: 0 });
  const blurTimer = useRef(null);
  const lastFocus = useRef({ slug: null, at: 0 });

  const cancelBlur = useCallback(() => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
  }, []);

  const focusThreeD = useCallback((slug) => {
    const next = threeD?.views?.[slug];
    if (!next) return; // a control without a camera is not a camera event: a pending blur camera still plays
    cancelBlur();
    lastFocus.current = { slug, at: Date.now() };
    setThreeDFocus((f) => ({ view: next, tick: f.tick + 1 }));
  }, [threeD, cancelBlur]);

  const blurThreeD = useCallback((slug) => {
    const next = threeD?.blurViews?.[slug];
    if (!next) return;
    const { slug: focusSlug, at } = lastFocus.current;
    if (focusSlug && focusSlug !== slug && Date.now() - at < BLUR_AFTER_FOCUS_IGNORE_MS) return; // caused by that control's tap
    cancelBlur();
    blurTimer.current = setTimeout(() => {
      blurTimer.current = null;
      setThreeDFocus((f) => ({ view: next, tick: f.tick + 1 }));
    }, BLUR_CAMERA_DELAY_MS);
  }, [threeD, cancelBlur]);

  useEffect(() => cancelBlur, [cancelBlur]);

  return { threeDFocus, focusThreeD, blurThreeD };
}
